import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import currencyFormatter from 'currency-formatter';

import {
  ONE_TIME_DISCOUNT_LABEL_TRANSLATION_KEY,
  FINITE_DISCOUNT_LABEL_TRANSLATION_KEY,
  CODE_DISCOUNT_TYPE,
} from '../../constants';
import { getCurrencyFormatter } from '../../../utils';
import { BOX_DISCOUNT_PREFIX } from '../../DiscountCodeCard/DiscountCodeCard';

const TableLabel = styled.span.attrs({
  className: 'bundle-table-label',
})`
  font-weight: 600;
  font-size: 16px;
`;

const TotalLabel = styled.p`
  text-align: right;
`;

const getDiscountLabel = ({
  type,
  label,
  recurringCycleLimit,
  usageCount,
  t,
  value,
}) => {
  const format = getCurrencyFormatter(value?.amount?.currencyCode);
  const prefix = label.startsWith(BOX_DISCOUNT_PREFIX)
    ? t(`tables.box_discount.${value?.percentage ? 'percentage' : 'amount'}`, {
        value: value?.percentage || format(value?.amount.amount),
      })
    : type === CODE_DISCOUNT_TYPE
    ? label
    : t(label);
  let suffix = '';

  if (recurringCycleLimit != null) {
    suffix =
      recurringCycleLimit === 1
        ? ONE_TIME_DISCOUNT_LABEL_TRANSLATION_KEY
        : FINITE_DISCOUNT_LABEL_TRANSLATION_KEY;
  }

  return t(`tables.subscription_discount`, {
    prefix,
    suffix: suffix
      ? t(`tables.${suffix}`, {
          recurringCycleLimit,
          // We need to add 1 to the usageCount as showing "0 of 2" is not
          // very user friendly, and we prefer to start the counting on 1
          // but we take this number from the usageCount property from
          // Shopify, which starts on 0 so...
          usageCount: usageCount + 1,
        })
      : '',
  });
};

const TableFoot = ({ subscription, isLoadingShippingRate = false }) => {
  const { t } = useTranslation();
  const isPrepaidV2 = subscription?.Lines?.some(({ PrepaidV2 }) => PrepaidV2);

  // Hide pricing if subscription has a gift
  if (subscription.ContainsGift) {
    return null;
  }

  return (
    <tfoot className="bundle-table__footer" data-testid="tablefoot">
      <tr className="bundle-table__footer-row bundle-table__footer-row--subtotal">
        <td colSpan={2} className="bundleapp-sm-hidden"></td>
        <td>
          <TableLabel>{t('tables.subtotal')}</TableLabel>
        </td>
        <td data-label={t('tables.subtotal')}>
          <TotalLabel>{subscription.FormattedSubscriptionSubtotal}</TotalLabel>
        </td>
      </tr>
      <tr className="bundle-table__footer-row bundle-table__footer-row--shipping">
        <td colSpan={2} className="bundleapp-sm-hidden"></td>
        <td>
          <TableLabel>{t('tables.shipping')}</TableLabel>
        </td>
        <td data-label={t('tables.shipping')}>
          <TotalLabel
            className={`${
              isLoadingShippingRate &&
              'bundleapp-loading bundleapp-loading--right'
            }`}
          >
            {subscription.FormattedDeliveryPrice}
          </TotalLabel>
        </td>
      </tr>
      {subscription.ShippingDiscountAmount > 0 && (
        <tr className="bundle-table__footer-row bundle-table__footer-row--shipping">
          <td colSpan={2} className="bundleapp-sm-hidden"></td>
          <td>
            <TableLabel>{subscription.ShippingDiscountLabel}</TableLabel>
          </td>
          <td data-label={t('tables.shippingDiscount')}>
            <TotalLabel>{subscription.FormattedShippingDiscount}</TotalLabel>
          </td>
        </tr>
      )}
      {subscription.SubscriptionLevelDiscount &&
        !isPrepaidV2 &&
        Object.keys(subscription.SubscriptionLevelDiscount).map((d) => {
          const {
            amount,
            label,
            currencyCode,
            type,
            recurringCycleLimit,
            usageCount,
            value,
          } = subscription.SubscriptionLevelDiscount[d];
          return (
            <tr
              key={label}
              className="bundle-table__footer-row bundle-table__footer-row--shipping"
            >
              <td colSpan={2} className="bundleapp-sm-hidden"></td>
              <td>
                <TableLabel data-testid="discount-label">
                  {getDiscountLabel({
                    type,
                    label,
                    recurringCycleLimit,
                    usageCount,
                    value,
                    t,
                  })}
                </TableLabel>
              </td>
              <td>
                <TotalLabel data-testid="discount-amount">
                  {currencyFormatter.format(amount, { code: currencyCode })}
                </TotalLabel>
              </td>
            </tr>
          );
        })}
      {isPrepaidV2 &&
        subscription.prepaidV2Discounts.length > 0 &&
        subscription.prepaidV2Discounts.map(
          ({ currentOrdersCount, amount, totalPrepaidV2Orders }, index) =>
            currentOrdersCount === 0 ? null : (
              <tr
                className="bundle-table__footer-row bundle-table__footer-row--shipping"
                key={index}
              >
                <td colSpan={2} className="bundleapp-sm-hidden"></td>
                <td>
                  <TableLabel>
                    {t('tables.prepaid_orders_discount', {
                      currentCount: currentOrdersCount,
                      totalCount: totalPrepaidV2Orders,
                    })}
                  </TableLabel>
                </td>
                <td>
                  <TotalLabel>{amount}</TotalLabel>
                </td>
              </tr>
            ),
        )}
      <tr className="bundle-table__footer-row bundle-table__footer-row--total">
        <td colSpan={2} className="bundleapp-sm-hidden"></td>
        <td>
          <TableLabel>{t('tables.total')}</TableLabel>
        </td>
        <td data-label={t('tables.total')}>
          <TotalLabel>{subscription.FormattedSubscriptionTotal}</TotalLabel>
        </td>
      </tr>
    </tfoot>
  );
};

export default TableFoot;
